import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import type { AdminMemberListItem, MemberRole, MemberStatus } from "@ticketnjoy/types";

import { MemberManageDialog, MemberRow } from "@/components/member-manage";
import { QueryError } from "@/components/query-error";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { MEMBER_PAGE_SIZE, useMembersQuery, type MemberListParams } from "@/features/api";
import { MEMBER_ROLE_LABELS, MEMBER_STATUS_LABELS } from "@/lib/format";

const ROLE_FILTERS: MemberRole[] = ["Owner", "Manager", "Editor", "Accountant", "Gate"];
const STATUS_FILTERS: MemberStatus[] = ["Active", "Invited", "Disabled"];
const ALL = "all";

type FilterUpdate = Partial<Pick<MemberListParams, "q" | "role" | "status" | "page">>;

/** Bütün organizatörlerin kullanıcıları; e-postayla bulup destek işlemi yapmak için. */
export function MembersPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const rawRole = searchParams.get("role") as MemberRole | null;
  const rawStatus = searchParams.get("status") as MemberStatus | null;
  const params: MemberListParams = {
    q: searchParams.get("q") ?? "",
    role: rawRole && ROLE_FILTERS.includes(rawRole) ? rawRole : "",
    status: rawStatus && STATUS_FILTERS.includes(rawStatus) ? rawStatus : "",
    organizerId: "",
    page: Math.max(1, Number(searchParams.get("page") ?? "1") || 1),
    pageSize: MEMBER_PAGE_SIZE,
  };

  const [searchInput, setSearchInput] = useState(params.q);
  const [selected, setSelected] = useState<AdminMemberListItem | null>(null);

  function update(next: FilterUpdate) {
    const merged = { q: params.q, role: params.role, status: params.status, page: 1, ...next };
    const nextParams = new URLSearchParams();
    for (const [key, value] of Object.entries(merged)) {
      if (!value || (key === "page" && value === 1)) continue;
      nextParams.set(key, String(value));
    }
    setSearchParams(nextParams, { replace: true });
  }

  useEffect(() => {
    const trimmed = searchInput.trim();
    if (trimmed === params.q) return;
    const t = setTimeout(() => update({ q: trimmed }), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sadece input değişince
  }, [searchInput]);

  const { data, isLoading, isFetching, error, refetch } = useMembersQuery(params);
  const pageCount = data ? Math.max(1, Math.ceil(data.total / MEMBER_PAGE_SIZE)) : 1;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Kullanıcılar</h1>
        <p className="text-sm text-muted-foreground">
          {data ? `${data.total.toLocaleString("tr-TR")} kayıt · ` : ""}
          Organizatör panellerine giren kişiler. Şifreyi hiçbir zaman sen belirlemezsin; kullanıcıya
          link gider. İşlemler organizatörün Aktivite ekranında "TicketNjoy" olarak görünür, kullanıcıya
          ayrıca e-posta gitmez.
        </p>
      </div>

      <div className="space-y-3">
        <div className="relative">
          <Search className="absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="pl-9"
            placeholder="E-posta veya organizatör adı"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Select
            value={params.role || ALL}
            onValueChange={(v) => update({ role: v === ALL ? "" : (v as MemberRole) })}
          >
            <SelectTrigger aria-label="Rol">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Tüm roller</SelectItem>
              {ROLE_FILTERS.map((r) => (
                <SelectItem key={r} value={r}>
                  {MEMBER_ROLE_LABELS[r]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={params.status || ALL}
            onValueChange={(v) => update({ status: v === ALL ? "" : (v as MemberStatus) })}
          >
            <SelectTrigger aria-label="Durum">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Tüm durumlar</SelectItem>
              {STATUS_FILTERS.map((s) => (
                <SelectItem key={s} value={s}>
                  {MEMBER_STATUS_LABELS[s]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Card className="py-0">
        <CardContent className="px-0">
          {error ? (
            <QueryError
              className="m-6"
              message="Liste yüklenemedi."
              onRetry={() => refetch()}
              retrying={isFetching}
            />
          ) : isLoading ? (
            <p className="p-6 text-sm text-muted-foreground">Yükleniyor...</p>
          ) : data && data.items.length === 0 ? (
            <p className="p-6 text-sm text-muted-foreground">Kayıt bulunamadı.</p>
          ) : (
            <ul className={isFetching ? "divide-y text-sm opacity-60" : "divide-y text-sm"}>
              {data?.items.map((m) => (
                <MemberRow key={m.id} member={m} showOrganizer onManage={() => setSelected(m)} />
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {pageCount > 1 && (
        <div className="flex items-center justify-end gap-2 text-sm">
          <Button
            variant="outline"
            size="sm"
            disabled={params.page <= 1}
            onClick={() => update({ page: params.page - 1 })}
          >
            Önceki
          </Button>
          <span className="text-muted-foreground">
            {params.page} / {pageCount}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={params.page >= pageCount}
            onClick={() => update({ page: params.page + 1 })}
          >
            Sonraki
          </Button>
        </div>
      )}

      <MemberManageDialog member={selected} onClose={() => setSelected(null)} />
    </div>
  );
}
